import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type HTMLAttributes, type LabelHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { CircleHelp, X, type LucideIcon } from 'lucide-react'
import { BrandLogo } from '@/components/brand/BrandLogo'
import { cn } from '@/lib/utils'

type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'

const toneClasses: Record<Tone, string> = {
  neutral: 'bg-[var(--mph-bg-control)] text-[var(--mph-text-secondary)] ring-[var(--mph-border)]',
  accent: 'bg-[var(--mph-accent-soft)] text-[var(--mph-accent)] ring-[var(--mph-accent-border)]',
  success: 'bg-[var(--mph-success-soft)] text-[var(--mph-success)] ring-[color:var(--mph-success)]/25',
  warning: 'bg-[var(--mph-warning-soft)] text-[var(--mph-warning)] ring-[color:var(--mph-warning)]/25',
  danger: 'bg-[var(--mph-danger-soft)] text-[var(--mph-danger)] ring-[color:var(--mph-danger)]/25',
  info: 'bg-[var(--mph-info-soft)] text-[var(--mph-info)] ring-[color:var(--mph-info)]/25',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
}

export const MpButton = forwardRef<HTMLButtonElement, ButtonProps>(function MpButton({
  asChild,
  className,
  variant = 'secondary',
  size = 'md',
  type = 'button',
  ...props
}, ref) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-[9px] border text-sm font-medium outline-none transition',
        'focus-visible:shadow-[var(--mph-focus-ring)] disabled:pointer-events-none disabled:opacity-45',
        variant === 'primary' &&
          'border-[var(--mph-accent-border)] bg-[var(--mph-accent)] text-[var(--mph-accent-contrast)] shadow-[0_8px_18px_var(--mph-accent-shadow)] hover:bg-[var(--mph-accent-hover)]',
        variant === 'secondary' &&
          'border-[var(--mph-border)] bg-[var(--mph-bg-control)] text-[var(--mph-text-primary)] hover:bg-[var(--mph-bg-control-hover)]',
        variant === 'ghost' &&
          'border-transparent bg-transparent text-[var(--mph-text-secondary)] hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-text-primary)]',
        variant === 'danger' &&
          'border-[color:var(--mph-danger)]/25 bg-[var(--mph-danger-soft)] text-[var(--mph-danger)] hover:brightness-110',
        size === 'sm' && 'h-8 px-3 text-[13px]',
        size === 'md' && 'h-10 px-3.5',
        size === 'lg' && 'h-11 px-4',
        className
      )}
      type={asChild ? undefined : type}
      {...props}
    />
  )
})

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  icon?: LucideIcon
  children?: ReactNode
  selected?: boolean
}

export const MpIconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function MpIconButton(
  { label, icon: Icon, children, selected, className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-transparent text-[var(--mph-text-secondary)] outline-none transition',
        'hover:border-[var(--mph-border)] hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-text-primary)]',
        'focus-visible:shadow-[var(--mph-focus-ring)] disabled:pointer-events-none disabled:opacity-45',
        selected && 'border-[var(--mph-accent-border)] bg-[var(--mph-accent-soft)] text-[var(--mph-accent)]',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-4 w-4" /> : children}
    </button>
  )
})

export function MpSegmentedControl({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  options: Array<{ value: string; label: string; icon?: ReactNode }>
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  className?: string
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('mph-scrollbar-hidden flex max-w-full gap-1 overflow-x-auto rounded-[10px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] p-1', className)}>
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-[7px] px-3 text-[13px] font-medium transition outline-none focus-visible:shadow-[var(--mph-focus-ring)]',
              selected
                ? 'border border-[var(--mph-accent-border)] bg-[var(--mph-accent-soft)] text-[var(--mph-accent)] shadow-sm'
                : 'border border-transparent text-[var(--mph-text-tertiary)] hover:bg-[var(--mph-bg-control-hover)] hover:text-[var(--mph-text-primary)]',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function MpPanel({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        'rounded-[16px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel)] shadow-[var(--mph-shadow-panel)] backdrop-blur-xl',
        className
      )}
      {...props}
    />
  )
}

export function MpWorkspaceFrame({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        'flex min-h-[calc(100svh-1rem)] flex-col overflow-hidden rounded-[18px] border border-[var(--mph-border)] bg-[var(--mph-bg-workspace)] shadow-[var(--mph-shadow-panel)] backdrop-blur-xl',
        className
      )}
      {...props}
    />
  )
}

export function MpPageToolbar({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex min-h-12 flex-wrap items-center justify-between gap-2 border-b border-[var(--mph-border-subtle)] bg-[var(--mph-bg-workspace)]/72 px-4 py-2',
        className
      )}
      {...props}
    />
  )
}

interface ListRowProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  interactive?: boolean
}

export function MpListRow({ selected, interactive = true, className, ...props }: ListRowProps) {
  return (
    <div
      className={cn(
        'group flex min-h-[52px] items-center gap-3 border-b border-[var(--mph-border-subtle)] px-3 py-2 text-sm text-[var(--mph-text-primary)] transition',
        interactive && 'hover:bg-[var(--mph-bg-control)]',
        selected && 'bg-[var(--mph-accent-soft)] shadow-[inset_2px_0_0_var(--mph-accent)]',
        className
      )}
      {...props}
    />
  )
}

export function MpInspectorPanel({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <aside
      className={cn(
        'min-h-0 border-l border-[var(--mph-border-subtle)] bg-[var(--mph-bg-panel)]/78 text-[var(--mph-text-primary)]',
        className
      )}
      {...props}
    />
  )
}

export function MpEmptyWorkspace({
  title,
  description,
  action,
  icon: Icon,
}: {
  title: string
  description: string
  action?: ReactNode
  icon?: LucideIcon
}) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center px-6 py-10 text-center">
      {Icon ? (
        <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-[12px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] text-[var(--mph-accent)]">
          <Icon className="h-5 w-5" />
        </span>
      ) : null}
      <p className="text-sm font-semibold text-[var(--mph-text-primary)]">{title}</p>
      <p className="mt-1 max-w-md text-sm leading-6 text-[var(--mph-text-secondary)]">{description}</p>
      {action ? <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  )
}

export function MpCard({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-[14px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel)] text-[var(--mph-text-primary)] shadow-[var(--mph-shadow-panel)]',
        className
      )}
      {...props}
    />
  )
}

export function MpCardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pt-5', className)} {...props} />
}

export function MpCardTitle({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('text-lg font-semibold tracking-tight text-[var(--mph-text-primary)]', className)} {...props} />
}

export function MpCardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-5', className)} {...props} />
}

export function MpPanelHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-start justify-between gap-4 border-b border-[var(--mph-border-subtle)] px-4 py-3', className)}
      {...props}
    />
  )
}

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
}

export function MpBadge({ tone = 'neutral', className, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium leading-5 ring-1',
        toneClasses[tone],
        className
      )}
      {...props}
    />
  )
}

interface StatusPillProps extends BadgeProps {
  dot?: boolean
}

export function MpStatusPill({ dot = true, tone = 'neutral', children, className, ...props }: StatusPillProps) {
  return (
    <MpBadge tone={tone} className={cn('pl-1.5', className)} {...props}>
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" /> : null}
      {children}
    </MpBadge>
  )
}

interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode
  value: string
  hint?: ReactNode
  tone?: Tone
  icon?: LucideIcon
}

export function MpMetricCard({ label, value, hint, tone = 'neutral', icon: Icon, className, ...props }: MetricCardProps) {
  return (
    <div
      className={cn('rounded-[13px] border border-[var(--mph-border-subtle)] bg-[var(--mph-bg-control)] p-3', className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-medium text-[var(--mph-text-tertiary)]">{label}</p>
        {Icon ? (
          <span className={cn('rounded-md p-1 ring-1', toneClasses[tone])}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-[var(--mph-text-primary)]">{value}</p>
      {hint ? <p className="mt-1 text-[13px] leading-5 text-[var(--mph-text-secondary)]">{hint}</p> : null}
    </div>
  )
}

interface AlertBannerProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone
  icon?: LucideIcon
  title: string
  onClose?: () => void
}

export function MpAlertBanner({ tone = 'info', icon: Icon, title, children, className, onClose, ...props }: AlertBannerProps) {
  return (
    <div
      className={cn('flex gap-3 rounded-[14px] border p-3 ring-1', toneClasses[tone], className)}
      {...props}
    >
      {Icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0" /> : null}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        {children ? <div className="mt-1 text-sm leading-6 text-[var(--mph-text-secondary)]">{children}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Приховати"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1 opacity-60 transition hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}

export function MpEmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="flex min-h-40 flex-col items-center justify-center rounded-[14px] border border-dashed border-[var(--mph-border)] px-6 py-8 text-center">
      <p className="text-sm font-semibold text-[var(--mph-text-primary)]">{title}</p>
      <p className="mt-1 max-w-md text-sm leading-6 text-[var(--mph-text-secondary)]">{description}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

export function MpSkeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-[var(--mph-bg-control)]', className)} {...props} />
}

export const MpInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function MpInput(
  { className, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-11 w-full rounded-[10px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] px-3.5 text-[15px] text-[var(--mph-text-primary)] outline-none transition',
        'placeholder:text-[var(--mph-text-tertiary)] focus-visible:border-[var(--mph-accent-border)] disabled:cursor-not-allowed disabled:opacity-55',
        className
      )}
      {...props}
    />
  )
})

export const MpCheckbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function MpCheckbox(
  { className, type: _type, ...props },
  ref,
) {
  return <input ref={ref} type="checkbox" className={cn('h-4 w-4 rounded border border-[var(--mph-border)] accent-[var(--mph-accent)] disabled:cursor-not-allowed disabled:opacity-55', className)} {...props} />
})

export const MpSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function MpSelect(
  { className, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        'mph-native-select h-10 w-full rounded-[10px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] pl-2.5 pr-8 text-sm text-[var(--mph-text-primary)] outline-none transition',
        'focus-visible:border-[var(--mph-accent-border)] disabled:cursor-not-allowed disabled:opacity-55',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  )
})

export function MpLabel({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('text-[15px] font-medium text-[var(--mph-text-secondary)]', className)} {...props} />
}

export function MpFieldError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return <p className="text-[13px] font-medium text-[var(--mph-danger)]">{children}</p>
}

export function MpPageHeader({
  title,
  eyebrow,
  description,
  descriptionTooltip,
  actions,
  className,
}: {
  title: string
  eyebrow?: string
  description?: string
  descriptionTooltip?: boolean
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-4 md:flex-row md:items-center md:justify-between', className)}>
      <div className="min-w-0">
        {eyebrow ? <p className="text-[13px] font-medium uppercase tracking-[0.16em] text-[var(--mph-text-tertiary)]">{eyebrow}</p> : null}
        <div className={cn('flex min-w-0 items-center gap-2', eyebrow && 'mt-1')}>
          <h1 className="min-w-0 text-2xl font-semibold tracking-tight text-[var(--mph-text-primary)] md:text-[28px]">{title}</h1>
          {description && descriptionTooltip ? (
            <span
              className="inline-flex h-6 w-6 shrink-0 translate-y-[1px] cursor-help items-center justify-center rounded-full leading-none text-[var(--mph-text-tertiary)] transition hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-text-secondary)]"
              title={description}
              aria-label={description}
            >
              <CircleHelp className="block h-4 w-4" strokeWidth={1.6} />
            </span>
          ) : null}
        </div>
        {description && !descriptionTooltip ? <p className="mt-2 text-sm leading-6 text-[var(--mph-text-secondary)]">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  )
}

export function MpAuthShell({
  title,
  description,
  children,
  asideTitle = 'Маркетплейс Помічник',
  asideDescription = 'Робоча панель для контролю товарів, позицій, конкурентів на маркетплейсах.',
  showAsideLogo = false,
}: {
  title: string
  description?: string
  children: ReactNode
  asideTitle?: string
  asideDescription?: string
  showAsideLogo?: boolean
}) {
  const authHighlights = [
    {
      title: 'Асортимент',
      description: 'Стежте за товарами, цінами та доступністю.',
    },
    {
      title: 'Видимість',
      description: 'Перевіряйте позиції у пошуку та категоріях.',
    },
    {
      title: 'Порівняння',
      description: 'Аналізуйте конкурентів і зміни на ринку.',
    },
  ]

  return (
    <div className="min-h-screen bg-[var(--mph-bg-app)] px-4 py-6 text-[var(--mph-text-primary)]">
      <div className="mx-auto grid min-h-[calc(100vh-3rem)] w-full max-w-6xl overflow-hidden rounded-[24px] border border-[var(--mph-border)] bg-[var(--mph-bg-shell)] shadow-[0_28px_90px_rgba(0,0,0,0.18)] lg:grid-cols-[minmax(0,1fr)_440px]">
        <section className="relative hidden overflow-hidden border-r border-[var(--mph-border)] bg-[radial-gradient(circle_at_30%_15%,var(--mph-accent-soft),transparent_34%),linear-gradient(150deg,var(--mph-bg-panel),var(--mph-bg-shell))] p-8 lg:block">
          <div className="relative z-10 flex h-full flex-col">
            <div>
              <h2 className="max-w-md text-4xl font-semibold tracking-tight text-[var(--mph-text-primary)]">
                {asideTitle}
              </h2>
              <p className="mt-4 max-w-md text-sm leading-6 text-[var(--mph-text-secondary)]">{asideDescription}</p>
            </div>
            {showAsideLogo ? (
              <div className="flex flex-1 items-center justify-center py-10" aria-hidden="true">
                <BrandLogo
                  showText={false}
                  variant="logo"
                  markClassName="h-auto w-[220px] drop-shadow-[0_24px_42px_rgba(15,111,141,0.16)] xl:w-64"
                />
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <div className="grid gap-3">
              {authHighlights.map((item) => (
                <div key={item.title} className="rounded-[16px] border border-[var(--mph-border-subtle)] bg-[var(--mph-bg-panel)] p-4 backdrop-blur">
                  <p className="text-sm font-medium text-[var(--mph-text-primary)]">{item.title}</p>
                  <p className="mt-1 text-[13px] text-[var(--mph-text-tertiary)]">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="flex items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-[420px]">
            <div className="mb-6 lg:hidden">
              <p className="text-sm font-semibold text-[var(--mph-text-primary)]">Маркетплейс Помічник</p>
              <p className="mt-1 text-[13px] leading-5 text-[var(--mph-text-tertiary)]">{asideDescription}</p>
            </div>
            <div className="rounded-[18px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel)] p-5 shadow-[var(--mph-shadow-panel)] backdrop-blur-xl sm:p-6">
              <div className="mb-5">
                <h1 className="text-2xl font-semibold tracking-tight text-[var(--mph-text-primary)]">{title}</h1>
                {description ? <p className="mt-2 text-sm leading-6 text-[var(--mph-text-secondary)]">{description}</p> : null}
              </div>
              {children}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
